/**
 * Customer review 11 Sep (V12): the supplier's NDA state for one customer,
 * inline on the customer card — "Sign NDA" until signed against the current
 * version, then "NDA signed on <date>". Self-contained: fetches its own
 * status and opens NDAModal, so a card only needs the customer id.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { FileSignature, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { onboardingAPI } from '../../lib/api';
import type { NDAStatus } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import NDAModal from './NDAModal';

interface NDAStatusControlProps {
  customerId: number;
  customerName?: string;
}

export default function NDAStatusControl({ customerId, customerName }: NDAStatusControlProps) {
  const { formatDate } = useFormatters();
  const [status, setStatus] = useState<NDAStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setFailed(false);
      const res = await onboardingAPI.getNDAStatus(customerId);
      setStatus(res.data);
    } catch (err: unknown) {
      console.error('Failed to load NDA status:', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> NDA
      </span>
    );
  }

  if (failed) {
    return (
      <button
        type="button"
        onClick={load}
        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
        title="Could not load NDA status — click to retry"
      >
        <AlertCircle className="h-3 w-3" /> NDA status unavailable
      </button>
    );
  }

  // The customer has not uploaded an NDA: nothing to sign, say nothing.
  if (!status?.nda) return null;

  if (status.agreement) {
    return (
      <>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1 text-xs text-green-700 hover:underline"
          title={`Version ${status.agreement.nda_version}, signed by ${status.agreement.signatory_name}`}
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          NDA signed on {formatDate(status.agreement.signed_at)}
        </button>
        <NDAModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          customerId={customerId}
          customerName={customerName}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setModalOpen(true)}
        className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
        title={`${customerName || 'This customer'} requires an NDA (version ${status.nda.version}) before you can see RFQ details`}
      >
        <FileSignature className="h-3.5 w-3.5 mr-1" />
        Sign NDA
      </Button>
      <NDAModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        customerId={customerId}
        customerName={customerName}
        onSigned={load}
      />
    </>
  );
}
